/**
 * Notities — lijst + pagina, Notion als lat (Joris, 25 sep 2026).
 *
 * Links een rustige kolom met vaste filters (Alle · Mijn · Snelle notities · Full Frame)
 * en de accounts/projecten die notities hebben. Rechts de lijst (titel, stil label, datum);
 * een klik opent de notitie als pagina met eigenschappen bovenaan en autosave.
 * "+ Nieuwe notitie" of N opent meteen een lege pagina met de cursor in de titel.
 * De notitie-URL is deelbaar: /notes?n=<id>.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, CircularProgress, IconButton, InputBase, Menu, MenuItem, Typography,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { bevestig } from '../../utils/bevestig';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { MARKDOWN_COMPONENTS } from '../../ui-components/MarkdownAfbeelding';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import MailNotitieTekst, { isMailNotitie } from '../../ui-components/MailNotitieTekst';
import NotitiePagina from './NotitiePagina';
import { INKT, STIL, LIJN, ACCENT, NaarProjectKnop } from './NotitieOnderdelen';
import { isNieuwSneltoets, korteDatum, maakBoomIndex, plekLabel, splitsTitel, titelUit, vindProject } from './notitieLogica';

// Zelfde markdown-weergave als de projectnotitie-kaarten (blijft export voor hergebruik).
export function NotitieTekst({ tekst, sx, onderwerp }) {
  if (isMailNotitie(tekst)) return <MailNotitieTekst tekst={tekst} sx={sx} onderwerp={onderwerp} />;
  return (
    <Typography component="div" variant="body2"
      sx={{ lineHeight: 1.6, '& p': { m: 0, mb: 0.5 }, '& ul': { m: 0, pl: 2.5 }, ...sx }}>
      <ReactMarkdown components={MARKDOWN_COMPONENTS}>{String(tekst || '')}</ReactMarkdown>
    </Typography>
  );
}

const KOLOM_BG = '#fafbfc';
const VASTE_FILTERS = [
  { type: 'alle', label: 'Alle notities' },
  { type: 'mijn', label: 'Mijn notities' },
  { type: 'los', label: 'Snelle notities', hint: 'nog nergens bij' },
  { type: 'fullframe', label: 'Full Frame', hint: 'intern' },
];

function KolomRij({ actief, diepte = 0, onClick, children, aantal, extra, stil }) {
  return (
    <ButtonBase component="div" onClick={onClick} sx={{
      display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5,
      pl: 1 + diepte * 1.75, pr: 1, py: 0.55, borderRadius: 1.25,
      bgcolor: actief ? '#eceff3' : 'transparent', '&:hover': { bgcolor: actief ? '#eceff3' : '#f1f3f5' },
      '&:hover .rij-extra': { opacity: 1 },
    }}>
      {children && typeof children === 'string' ? (
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: stil ? STIL : INKT, fontWeight: actief ? 600 : 400 }}>{children}</Typography>
      ) : children}
      {extra}
      {aantal > 0 && <Typography sx={{ fontSize: 12, color: STIL, ml: 0.5 }}>{aantal}</Typography>}
    </ButtonBase>
  );
}

function KolomKop({ children, rechts }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pr: 0.5, mt: 2.5, mb: 0.5 }}>
      <Typography sx={{ flex: 1, fontSize: 11.5, fontWeight: 600, color: STIL, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{children}</Typography>
      {rechts}
    </Box>
  );
}

export default function Notities() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const nParam = searchParams.get('n');
  const { accounts } = useMsal();
  const mijnNaam = accounts[0]?.name || '';

  const [boom, setBoom] = useState(null);
  const boomIndex = useMemo(() => maakBoomIndex(boom), [boom]);
  const [scope, setScope] = useState({ type: 'alle' });
  const [notes, setNotes] = useState(null);
  const [alleenMijn, setAlleenMijn] = useState(() => { try { return localStorage.getItem('notes:alleenMijn') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('notes:alleenMijn', alleenMijn ? '1' : '0'); } catch { /* privémodus */ } }, [alleenMijn]);
  const [weergave, setWeergave] = useState('account'); // 'account' | 'project'
  const [openAccounts, setOpenAccounts] = useState(new Set());
  const [overigOpen, setOverigOpen] = useState(false);
  const [overigFilter, setOverigFilter] = useState('');
  const [alleProjectenVan, setAlleProjectenVan] = useState(() => new Set());
  const [meerAnker, setMeerAnker] = useState(null);

  // Open pagina: { sleutel, notitie | null (nieuw), startPlek }
  const [open, setOpen] = useState(null);
  const sleutelRef = useRef(0);
  const eigenIdRef = useRef(null); // id die de open pagina zelf net aanmaakte (URL volgt, geen remount)
  // Generatie van de editor die nú open is; callbacks van een verlaten pagina mogen hun save
  // afronden maar niet meer de selectie of URL wijzigen (Codex 25 sep, punt 6).
  // Wordt alleen expliciet gezet (openen/sluiten/URL-navigatie), nooit per render — anders zou
  // een verlaten editor bij de volgende render weer 'actief' worden.
  const actieveSleutelRef = useRef(null);
  const nParamRef = useRef(nParam); nParamRef.current = nParam;
  const opgehaaldRef = useRef(new Map()); // id → notitie, gericht op id opgehaald (deellinks)
  const lopendRef = useRef(new Set()); // ids waarvan het ophalen nu loopt

  // ── Zoeken ──
  const [zoek, setZoek] = useState('');
  const [treffers, setTreffers] = useState(null);
  const zoekRef = useRef(null);
  const zoekActief = zoek.trim().length >= 2;
  useEffect(() => {
    if (!zoekActief) { setTreffers(null); return undefined; }
    let dood = false;
    setTreffers(undefined);
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/zoek?q=${encodeURIComponent(zoek.trim())}${alleenMijn ? `&mine=1&mine_name=${encodeURIComponent(mijnNaam)}` : ''}`);
        const d = await leesJson(r, 'Notities zoeken');
        if (!dood) setTreffers(d.notes || []);
      } catch (e) { if (!dood) { meldFout(e, 'Notities zoeken'); setTreffers([]); } }
    }, 250);
    return () => { dood = true; clearTimeout(t); };
  }, [zoek, zoekActief, alleenMijn, mijnNaam]);

  // ── Laden ──
  const laadBoom = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/boom`);
      const d = await leesJson(r, 'Notitieboom laden');
      setBoom(d.accounts || []);
    } catch (e) { meldFout(e, 'Notitieboom laden'); setBoom([]); }
  }, []);
  useEffect(() => { laadBoom(); }, [laadBoom]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const laadNotes = useCallback(async (sel) => {
    setNotes(null);
    try {
      let url;
      if (sel.type === 'alle') {
        url = `${BACKEND_URL}/Api/project-notes/recent`;
      } else if (sel.type === 'mijn') {
        // Alles van mij: zoeken op mijn naam, gefilterd op auteur (de engine kent mijn naamvarianten).
        url = mijnNaam
          ? `${BACKEND_URL}/Api/project-notes/zoek?q=${encodeURIComponent(mijnNaam)}&mine=1&mine_name=${encodeURIComponent(mijnNaam)}`
          : `${BACKEND_URL}/Api/project-notes/recent`;
      } else {
        const params = new URLSearchParams({ scope: sel.type });
        if (sel.account) params.set('account', sel.account);
        if (sel.folder) params.set('folder', sel.folder);
        if (sel.projectId) params.set('projectId', sel.projectId);
        if (alleenMijn) { params.set('mine', '1'); params.set('mine_name', mijnNaam); }
        url = `${BACKEND_URL}/Api/project-notes/overzicht?${params}`;
      }
      const r = await authFetch(url);
      const d = await leesJson(r, 'Notities laden');
      let lijst = d.notes || [];
      if (sel.type === 'alle' && alleenMijn && mijnNaam) lijst = lijst.filter((n) => n.door === mijnNaam);
      setNotes(lijst);
    } catch (e) { meldFout(e, 'Notities laden'); setNotes([]); }
  }, [alleenMijn, mijnNaam]);
  useEffect(() => { laadNotes(scope); }, [scope, laadNotes]);

  // ── Openen / nieuw / terug (URL = ?n=<id> of ?n=nieuw) ──
  const startPlekVan = (sel) => {
    if (sel.type === 'project') return { type: 'project', projectId: sel.projectId, account: sel.account };
    if (sel.type === 'account') return { type: 'account', account: sel.account, folder: sel.folder || '' };
    if (sel.type === 'fullframe') return { type: 'fullframe' };
    return { type: 'los' };
  };
  const openNotitie = (n) => {
    sleutelRef.current += 1;
    eigenIdRef.current = n.id;
    actieveSleutelRef.current = sleutelRef.current;
    setOpen({ sleutel: sleutelRef.current, notitie: n });
    setSearchParams({ n: n.id });
  };
  const nieuweNotitie = useCallback(() => {
    sleutelRef.current += 1;
    eigenIdRef.current = null;
    actieveSleutelRef.current = sleutelRef.current;
    setOpen({ sleutel: sleutelRef.current, notitie: null, startPlek: startPlekVan(scope) });
    setSearchParams({ n: 'nieuw' });
  }, [scope, setSearchParams]);
  const terug = () => {
    actieveSleutelRef.current = null;
    setOpen(null);
    setSearchParams({});
    laadNotes(scope);
    laadBoom();
  };

  const toonNotitie = (n) => {
    sleutelRef.current += 1;
    eigenIdRef.current = n.id;
    actieveSleutelRef.current = sleutelRef.current;
    setOpen({ sleutel: sleutelRef.current, notitie: n });
  };

  // URL → staat (terugknop van de browser, gedeelde link).
  useEffect(() => {
    const sluitEditor = () => { actieveSleutelRef.current = null; eigenIdRef.current = null; setOpen(null); };
    if (!nParam) { if (open) { sluitEditor(); laadNotes(scope); } return; }
    if (open) {
      const zelfde = nParam === 'nieuw'
        ? (!open.notitie && !eigenIdRef.current)
        : (nParam === eigenIdRef.current || nParam === open.notitie?.id);
      if (zelfde) return;
      // Naar een andere notitie genavigeerd: de vorige editor meteen ongeldig maken, zodat een
      // late aanmaak-response de URL niet meer kan terugzetten (Codex-hercheck 25 sep).
      actieveSleutelRef.current = null;
      eigenIdRef.current = null;
    }
    if (nParam === 'nieuw') {
      sleutelRef.current += 1;
      actieveSleutelRef.current = sleutelRef.current;
      setOpen({ sleutel: sleutelRef.current, notitie: null, startPlek: startPlekVan(scope) });
      return;
    }
    const lijst = [...(notes || []), ...(Array.isArray(treffers) ? treffers : [])];
    const n = lijst.find((x) => x.id === nParam) || opgehaaldRef.current.get(nParam);
    if (n) { toonNotitie(n); return; }
    if (open) sluitEditor(); // niet onder een vreemde URL laten staan terwijl we laden
    if (notes === null || lopendRef.current.has(nParam)) return;
    // Niet in de geladen lijst (bijv. een oudere notitie via een gedeelde link): gericht op id ophalen.
    const gezocht = nParam;
    lopendRef.current.add(gezocht);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${encodeURIComponent(gezocht)}`);
        if (r.status === 404) {
          if (nParamRef.current === gezocht) { toast.error('Notitie niet gevonden'); setSearchParams({}, { replace: true }); }
          return;
        }
        const d = await leesJson(r, 'Notitie laden');
        const gevonden = d?.note || (d?.id ? d : null);
        if (gevonden?.id === gezocht) opgehaaldRef.current.set(gezocht, gevonden);
        if (nParamRef.current !== gezocht) return; // intussen elders heen genavigeerd
        if (gevonden?.id === gezocht) toonNotitie(gevonden);
        else { toast.error('Notitie niet gevonden'); setSearchParams({}, { replace: true }); }
      } catch (e) {
        // Geen 404: de notitie bestaat misschien wel; de link laten staan.
        if (nParamRef.current === gezocht) meldFout(e, 'Notitie laden');
      } finally {
        lopendRef.current.delete(gezocht);
      }
    })();
  }, [nParam, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  const naAangemaakt = useCallback((sleutel, id) => {
    if (actieveSleutelRef.current !== sleutel) return; // pagina al verlaten: selectie/URL niet meer aanraken
    eigenIdRef.current = id;
    setSearchParams({ n: id }, { replace: true });
  }, [setSearchParams]);
  const openSleutel = open?.sleutel;
  const opAangemaakt = useCallback((id) => naAangemaakt(openSleutel, id), [naAangemaakt, openSleutel]);
  const naGewijzigd = useCallback((deel) => {
    if (!deel?.id) return;
    const pas = (prev) => {
      if (!Array.isArray(prev)) return prev;
      if (prev.some((n) => n.id === deel.id)) return prev.map((n) => (n.id === deel.id ? { ...n, ...deel } : n));
      return [{ door: mijnNaam, at: new Date().toISOString(), ...deel }, ...prev];
    };
    setNotes(pas);
  }, [mijnNaam]);
  const naVerwijderd = (id) => {
    setNotes((prev) => (prev || []).filter((n) => n.id !== id));
    setTreffers((prev) => (Array.isArray(prev) ? prev.filter((n) => n.id !== id) : prev));
    terug();
  };

  // Sneltoetsen: N = nieuwe notitie, ⌘K = zoeken.
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        if (open) terug();
        setTimeout(() => { zoekRef.current?.focus(); zoekRef.current?.select(); }, 0);
        return;
      }
      if (!open && isNieuwSneltoets(e, document.activeElement) && !document.querySelector('[role="dialog"],[role="presentation"]')) {
        e.preventDefault();
        nieuweNotitie();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }); // bewust elke render: leest de actuele staat

  const kies = (sel) => { setScope(sel); setZoek(''); if (open) { actieveSleutelRef.current = null; setOpen(null); setSearchParams({}); } };

  const verwijderUitLijst = async (n) => {
    if (!(await bevestig({ titel: 'Notitie verwijderen?', tekst: `"${n.onderwerp || 'Notitie'}" wordt verwijderd. Dit kan niet ongedaan worden gemaakt.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${n.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Verwijderen mislukt');
      toast.success('Notitie verwijderd');
      setNotes((prev) => (prev || []).filter((x) => x.id !== n.id));
      setTreffers((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== n.id) : prev));
      laadBoom();
    } catch (e) { toast.error(e.message); }
  };

  // ── Mappen (vrij aan te maken per account) ──
  const [nieuweMapVoor, setNieuweMapVoor] = useState(null); // account waar de inline invoer open staat
  const [nieuweMapNaam, setNieuweMapNaam] = useState('');
  const sluitNieuweMap = () => { setNieuweMapVoor(null); setNieuweMapNaam(''); };
  const nieuweMap = async (account, naam) => {
    sluitNieuweMap();
    if (!naam || !naam.trim()) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/mappen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account, map: naam.trim() }),
      });
      if (!r.ok) throw new Error();
      laadBoom();
      setOpenAccounts(new Set([account]));
    } catch { toast.error('Map aanmaken mislukt'); }
  };
  const verwijderMap = async (account, naam) => {
    if (!(await bevestig({ titel: `Map "${naam}" verwijderen?`, tekst: 'Notities erin verhuizen naar Algemeen.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/project-notes/mappen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account, map: naam, actie: 'remove' }),
      });
      laadBoom();
      if (scope.folder === naam) kies({ type: 'account', account });
    } catch { toast.error('Verwijderen mislukt'); }
  };

  // ── Kolom: accounts mét notities voorop, de rest ingeklapt ──
  const { gebruikt, overig } = useMemo(() => {
    const g = []; const o = [];
    (boom || []).forEach((acc) => {
      const inGebruik = (acc.notities || 0) > 0 || (acc.mappen || []).length > 0 || scope.account === acc.account;
      (inGebruik ? g : o).push(acc);
    });
    const f = overigFilter.trim().toLowerCase();
    return { gebruikt: g, overig: f ? o.filter((a) => a.account.toLowerCase().includes(f)) : o };
  }, [boom, scope.account, overigFilter]);
  const projectenMetNotities = useMemo(() => [...boomIndex.projecten.values()]
    .filter((p) => p.aantal > 0)
    .sort((a, b) => String(b.nummer).localeCompare(String(a.nummer))), [boomIndex]);

  const isActief = (sel) => scope.type === sel.type
    && (scope.account || '') === (sel.account || '')
    && (scope.folder || '') === (sel.folder || '')
    && (scope.projectId || '') === (sel.projectId || '');
  const toggleAccount = (account) => setOpenAccounts((prev) => (prev.has(account) ? new Set() : new Set([account])));

  const renderAccount = (acc) => {
    const isOpen = openAccounts.has(acc.account);
    const Pijl = isOpen ? ExpandMoreIcon : ChevronRightIcon;
    const metNotities = acc.projecten.filter((p) => p.aantal > 0 || isActief({ type: 'project', projectId: p.id }));
    const alles = alleProjectenVan.has(acc.account);
    const zichtbaar = alles ? acc.projecten : metNotities;
    const verborgen = acc.projecten.length - zichtbaar.length;
    return (
      <Box key={acc.account}>
        <KolomRij onClick={() => toggleAccount(acc.account)} aantal={acc.notities}>
          <Pijl sx={{ fontSize: 16, color: STIL, ml: -0.5 }} />
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: INKT }}>{acc.account}</Typography>
        </KolomRij>
        {isOpen && (
          <>
            <KolomRij diepte={1} actief={isActief({ type: 'account', account: acc.account })} aantal={acc.mapAantal?.Algemeen}
              onClick={() => kies({ type: 'account', account: acc.account, label: acc.account })}>Algemeen</KolomRij>
            {(acc.mappen || []).map((m) => (
              <KolomRij key={m} diepte={1} actief={isActief({ type: 'account', account: acc.account, folder: m })} aantal={acc.mapAantal?.[m]}
                onClick={() => kies({ type: 'account', account: acc.account, folder: m, label: `${acc.account} · ${m}` })}
                extra={(
                  <IconButton className="rij-extra" size="small" aria-label={`Map ${m} verwijderen`} sx={{ opacity: 0, p: 0.25 }}
                    onClick={(e) => { e.stopPropagation(); verwijderMap(acc.account, m); }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14, color: STIL }} />
                  </IconButton>
                )}>
                {m}
              </KolomRij>
            ))}
            {zichtbaar.map((p) => (
              <KolomRij key={p.id} diepte={1} actief={isActief({ type: 'project', projectId: p.id })} aantal={p.aantal} stil={!p.aantal}
                onClick={() => kies({ type: 'project', projectId: p.id, account: acc.account, label: `${p.nummer} · ${p.naam}` })}>
                {`${p.nummer ? `${p.nummer} ` : ''}${p.naam}`}
              </KolomRij>
            ))}
            {verborgen > 0 && (
              <KolomRij diepte={1} stil onClick={() => setAlleProjectenVan((prev) => new Set(prev).add(acc.account))}>
                {`${verborgen} project${verborgen === 1 ? '' : 'en'} zonder notities`}
              </KolomRij>
            )}
            {nieuweMapVoor === acc.account ? (
              <Box sx={{ pl: 1 + 1.75, pr: 1, py: 0.25 }}>
                <InputBase autoFocus fullWidth value={nieuweMapNaam} placeholder="Naam van de map…"
                  onChange={(e) => setNieuweMapNaam(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); nieuweMap(acc.account, nieuweMapNaam); }
                    if (e.key === 'Escape') { e.preventDefault(); sluitNieuweMap(); }
                  }}
                  onBlur={() => { if (!nieuweMapNaam.trim()) sluitNieuweMap(); }}
                  inputProps={{ 'aria-label': `Nieuwe map onder ${acc.account}`, title: 'Enter = aanmaken, Esc = annuleren' }}
                  sx={{ px: 1, fontSize: 13, bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: 1.25 }} />
              </Box>
            ) : (
              <KolomRij diepte={1} stil onClick={() => { setNieuweMapVoor(acc.account); setNieuweMapNaam(''); }}>+ map</KolomRij>
            )}
          </>
        )}
      </Box>
    );
  };

  const lijst = zoekActief ? treffers : notes;
  const scopeTitel = zoekActief ? `Zoeken: “${zoek.trim()}”`
    : scope.type === 'alle' ? 'Alle notities'
      : scope.type === 'mijn' ? 'Mijn notities'
        : scope.type === 'los' ? 'Snelle notities'
          : scope.type === 'fullframe' ? 'Full Frame'
            : scope.label || scope.account || 'Notities';

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, md: 4 }, pb: 6 }}>
      <PageHeader dense icon="sticky_note_2" title="Notities"
        rightActions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={nieuweNotitie}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
              Nieuwe notitie
            </Button>
            <IconButton size="small" aria-label="Meer" onClick={(e) => setMeerAnker(e.currentTarget)} sx={{ color: STIL }}>
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={meerAnker} open={!!meerAnker} onClose={() => setMeerAnker(null)}
              PaperProps={{ elevation: 0, sx: { border: `1px solid ${LIJN}`, boxShadow: '0 8px 24px rgba(15,23,42,0.10)' } }}>
              <MenuItem onClick={() => { setMeerAnker(null); navigate('/notes/onenote-import'); }} sx={{ fontSize: 13.5 }}>OneNote importeren…</MenuItem>
            </Menu>
          </Box>
        )} />

      <Box sx={{ display: 'flex', border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden', minHeight: 'calc(100vh - 190px)' }}>
        {/* ── Kolom ── */}
        <Box sx={{ width: 264, flexShrink: 0, bgcolor: KOLOM_BG, borderRight: `1px solid ${LIJN}`, p: 1.25,
          display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
          {VASTE_FILTERS.map((f) => (
            <KolomRij key={f.type} actief={!zoekActief && scope.type === f.type} onClick={() => kies({ type: f.type })}>{f.label}</KolomRij>
          ))}

          <KolomKop rechts={(
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              {[['account', 'Account'], ['project', 'Project']].map(([k, l]) => (
                <ButtonBase key={k} onClick={() => setWeergave(k)}
                  sx={{ px: 0.75, py: 0.1, borderRadius: 1, fontSize: 11.5, color: weergave === k ? INKT : STIL, fontWeight: weergave === k ? 600 : 400,
                    bgcolor: weergave === k ? '#eceff3' : 'transparent' }}>
                  {l}
                </ButtonBase>
              ))}
            </Box>
          )}>
            Per
          </KolomKop>

          {boom === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={16} /></Box>
          ) : weergave === 'project' ? (
            projectenMetNotities.length === 0
              ? <Typography sx={{ fontSize: 12.5, color: STIL, pl: 1, py: 0.5 }}>Nog geen project met notities.</Typography>
              : projectenMetNotities.map((p) => (
                <KolomRij key={p.id} actief={isActief({ type: 'project', projectId: p.id })} aantal={p.aantal}
                  onClick={() => kies({ type: 'project', projectId: p.id, account: p.account, label: `${p.nummer} · ${p.naam}` })}>
                  {`${p.nummer ? `${p.nummer} ` : ''}${p.naam}`}
                </KolomRij>
              ))
          ) : (
            <>
              {gebruikt.map(renderAccount)}
              {gebruikt.length === 0 && <Typography sx={{ fontSize: 12.5, color: STIL, pl: 1, py: 0.5 }}>Nog geen account met notities.</Typography>}
              {(overig.length > 0 || overigFilter) && (
                <Box sx={{ mt: 1 }}>
                  <KolomRij stil onClick={() => setOverigOpen((v) => !v)} aantal={overig.length}>
                    {overigOpen ? <ExpandMoreIcon sx={{ fontSize: 16, color: STIL, ml: -0.5 }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: STIL, ml: -0.5 }} />}
                    <Typography noWrap sx={{ flex: 1, fontSize: 13.5, color: STIL }}>Overige accounts</Typography>
                  </KolomRij>
                  {overigOpen && (
                    <Box sx={{ pl: 1 }}>
                      <InputBase fullWidth placeholder="Filter accounts…" value={overigFilter} onChange={(e) => setOverigFilter(e.target.value)}
                        sx={{ my: 0.5, px: 1, fontSize: 13, bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: 1.25 }} />
                      {overig.slice(0, 60).map(renderAccount)}
                      {overig.length > 60 && <Typography sx={{ fontSize: 12, color: STIL, pl: 1 }}>… nog {overig.length - 60}, typ om te filteren</Typography>}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* ── Lijst of pagina ── */}
        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 6 }, py: { xs: 2.5, md: 4 } }}>
          {open ? (
            <NotitiePagina key={open.sleutel} notitie={open.notitie} startPlek={open.startPlek}
              boom={boom} boomIndex={boomIndex} mijnNaam={mijnNaam}
              onTerug={terug} onAangemaakt={opAangemaakt} onGewijzigd={naGewijzigd} onVerwijderd={naVerwijderd} />
          ) : (
            <Box sx={{ maxWidth: 880, mx: 'auto' }}>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                {VASTE_FILTERS.map((f) => (
                  <ButtonBase key={f.type} onClick={() => kies({ type: f.type })}
                    sx={{ px: 1, py: 0.4, borderRadius: 1, fontSize: 13, color: scope.type === f.type ? INKT : STIL, bgcolor: scope.type === f.type ? '#eceff3' : 'transparent' }}>
                    {f.label}
                  </ButtonBase>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5, mb: 2.5, borderRadius: 1.5, border: `1px solid ${LIJN}`,
                '&:focus-within': { borderColor: '#c3cad5' } }}>
                <SearchIcon sx={{ fontSize: 18, color: STIL }} />
                <InputBase inputRef={zoekRef} fullWidth value={zoek} onChange={(e) => setZoek(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setZoek(''); }}
                  placeholder="Zoek in notities…" inputProps={{ 'aria-label': 'Zoek in notities' }} sx={{ fontSize: 14 }} />
                {zoek ? (
                  <IconButton size="small" onClick={() => setZoek('')} aria-label="Zoekopdracht wissen"><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                ) : <Typography sx={{ fontSize: 12, color: '#b6bec9', whiteSpace: 'nowrap' }}>⌘K</Typography>}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1, px: 1 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: INKT, flex: 1, minWidth: 0 }} noWrap>{scopeTitel}</Typography>
                {!zoekActief && scope.type === 'project' && scope.projectId && (
                  <NaarProjectKnop projectId={scope.projectId} navigate={navigate} />
                )}
                {scope.type !== 'mijn' && (
                  <ButtonBase onClick={() => setAlleenMijn((v) => !v)}
                    sx={{ fontSize: 12.5, px: 0.75, py: 0.25, borderRadius: 1, color: alleenMijn ? INKT : STIL, fontWeight: alleenMijn ? 600 : 400,
                      bgcolor: alleenMijn ? '#eceff3' : 'transparent', '&:hover': { bgcolor: '#f1f3f5' } }}>
                    Alleen van mij
                  </ButtonBase>
                )}
                {Array.isArray(lijst) && <Typography sx={{ fontSize: 12.5, color: STIL }}>{lijst.length}{lijst.length >= 100 ? '+' : ''}</Typography>}
              </Box>

              {lijst == null ? <Box sx={{ px: 1, py: 2 }}><CircularProgress size={18} /></Box> : (
                <Box>
                  {lijst.length === 0 && (
                    <Typography sx={{ fontSize: 13.5, color: STIL, px: 1, py: 2 }}>
                      {zoekActief ? 'Niets gevonden.' : 'Nog geen notities hier.'}
                    </Typography>
                  )}
                  {lijst.map((n) => {
                    const { titel } = splitsTitel(n.onderwerp, n.notes);
                    const label = plekLabel(n, boomIndex);
                    return (
                      <ButtonBase key={n.id} component="div" onClick={() => openNotitie(n)} sx={{
                        display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5,
                        px: 1, py: 0.9, borderRadius: 1.25, borderBottom: `1px solid ${LIJN}`,
                        '&:hover': { bgcolor: '#f7f8fa' }, '&:hover .rij-extra': { opacity: 1 },
                      }}>
                        <Typography noWrap sx={{ fontSize: 14.5, color: INKT, minWidth: 0, flexShrink: 1 }}>{titel || titelUit(n.notes)}</Typography>
                        {label && (
                          <Typography noWrap component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (n.projectId) { const p = vindProject(boomIndex, n.projectId); if (p) { kies({ type: 'project', projectId: p.id, account: p.account, label }); return; } }
                              if (n.klant === 'Full Frame') { kies({ type: 'fullframe' }); return; }
                              if (n.klant) kies({ type: 'account', account: n.klant, folder: n.folder || undefined, label });
                            }}
                            title="Toon alle notities hier"
                            sx={{ fontSize: 12.5, color: STIL, minWidth: 0, flexShrink: 2, '&:hover': { color: INKT, textDecoration: 'underline' } }}>
                            {label}
                          </Typography>
                        )}
                        <Box sx={{ flex: 1 }} />
                        <IconButton className="rij-extra" size="small" aria-label="Notitie verwijderen"
                          onClick={(e) => { e.stopPropagation(); verwijderUitLijst(n); }}
                          sx={{ opacity: 0, p: 0.25, color: STIL, '&:hover': { color: '#b91c1c' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 12.5, color: STIL, whiteSpace: 'nowrap', minWidth: 52, textAlign: 'right' }}>{korteDatum(n.datum || n.at)}</Typography>
                      </ButtonBase>
                    );
                  })}
                  {!zoekActief && (
                    <ButtonBase onClick={nieuweNotitie} sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, width: '100%', px: 1, py: 1, borderRadius: 1.25, color: STIL, '&:hover': { bgcolor: '#f7f8fa', color: INKT } }}>
                      <AddIcon sx={{ fontSize: 17 }} />
                      <Typography sx={{ fontSize: 14 }}>Nieuwe notitie</Typography>
                      <Typography sx={{ fontSize: 12, color: '#b6bec9', ml: 'auto' }}>N</Typography>
                    </ButtonBase>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
